import { QuickbooksSyncResult, UpdateQuickbooksInvoice } from '@apis/Invoices/model';
import { Box, Button, CopyButton, Divider, Group, LoadingOverlay, Space, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { useDi, useDiContainer } from '@root/Services/DI';
import { useEventValue } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { useMemo } from 'react';
import { AlertTriangle, Check, CircleCheck, CirclePlus, Copy } from 'tabler-icons-react';
import { ActivityDetailsModel } from './ActivityDetailsService';
import { ActivityDetailsContainer, DetailRow, ProgressBadges, RowDivider } from './Design';
import { ActivityDetailsProps } from './Models';

export function QuickbooksActivityDetails({ item: { job: item } }: ActivityDetailsProps) {
    const container = useDiContainer();
    const formatSvc = useDi(FormatService);
    const model = useMemo(() => {
        const result = container.resolve(ActivityDetailsModel);
        result.init(item);
        return result;
    }, []);
    const loading = useEventValue(model.loading);
    const status = model.statusInfo;

    const params = item.Parameters as unknown as UpdateQuickbooksInvoice;
    const itemDetails = item?.ResultDetail as unknown as QuickbooksSyncResult | undefined;
    const syncErrors = item?.FailureMessage;

    return loading ? (
        <LoadingOverlay visible />
    ) : (
        <ActivityDetailsContainer>
            {status?.inProgress ? (
                <DetailRow title="Started">{formatSvc.toLocal(item.StartedAt)}</DetailRow>
            ) : (
                <DetailRow title="Finished">{formatSvc.toLocal(model.progress?.lastDate)}</DetailRow>
            )}
            <RowDivider />
            <DetailRow title="Status">
                <ProgressBadges progress={model.progress} />
            </DetailRow>
            <RowDivider />
            <DetailRow title="Invoice">{params.Title}</DetailRow>
            {status?.inProgress ? null : (
                <>
                    <DetailRow title="QuickBooks Sync">
                        <Text size="sm">
                            {itemDetails?.InvoiceCreated ? 'Invoice Created' : itemDetails?.InvoiceUpdated ? 'Invoice Updated' : 'No Invoice Changes'}
                        </Text>
                    </DetailRow>
                    {!itemDetails?.QbInvoiceId ? null : <DetailRow title="QB Invoice Id">{itemDetails?.QbInvoiceId}</DetailRow>}
                    <DetailRow title="Line Items">{formatSvc.formatInt(itemDetails?.LineItemCount ?? 0)}</DetailRow>
                    <DetailRow title="Has Failures">{itemDetails?.HasFailures || syncErrors ? 'Yes' : 'No'}</DetailRow>
                    {!syncErrors ? null : (
                        <DetailRow alignTop title="Failure Message">
                            {syncErrors}
                        </DetailRow>
                    )}
                    <Space h="sm" />
                    <Text size="sm">Item Sync Details</Text>
                    <Divider mx={-24} />
                    <Box mx={-24} mb={-24} p="lg" sx={{ flex: 1, overflow: 'auto' }}>
                        <ProductCreationResults items={itemDetails?.ItemCreationResults} />
                        <LineItemFailures items={itemDetails?.LineItemFailure} />
                    </Box>
                </>
            )}
        </ActivityDetailsContainer>
    );
}

function ProductCreationResults({ items }: { items: QuickbooksSyncResult['ItemCreationResults'] }) {
    const productDetails = useMemo(() => {
        return (items ?? []).map((r) => ({
            status: r.ErrorMessage ? 'failure' : 'success',
            name: r.Name?.trim() || '[Unnamed Product]',
            message: r.ErrorMessage?.trim() || undefined,
            description: r.Description ?? '[Empty Description]',
        }));
    }, [items]);

    return (
        <>
            <Text size="xs" weight="bold" color="warning">
                New Products
            </Text>
            {!productDetails.length ? (
                <Text size="sm" color="dimmed" mt="sm">
                    No products need were created
                </Text>
            ) : (
                productDetails.map((entry, idx) => (
                    <>
                        {idx === 0 ? null : <Divider color="gray.3" />}
                        <Group key={`product-${entry.name}-${idx}`} spacing="xs" noWrap>
                            <ThemeIcon variant="light" color={entry.status === 'success' ? 'success' : 'error'} radius="xl" size="sm">
                                {entry.status === 'success' ? <CircleCheck size={16} /> : <AlertTriangle size={16} />}
                            </ThemeIcon>
                            <Box sx={{ flex: 1 }}>
                                <CopyButton value={entry.description}>
                                    {({ copied, copy }) => (
                                        <Group position="apart" noWrap>
                                            <Text size="sm" sx={{ whiteSpace: 'nowrap' }}>
                                                {entry.description}
                                            </Text>
                                            <Tooltip withinPortal withArrow position="bottom" label={copied ? 'Copied' : 'Copy Product Name'}>
                                                <Button variant="subtle" onClick={copy}>
                                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                                </Button>
                                            </Tooltip>
                                        </Group>
                                    )}
                                </CopyButton>
                                <Box sx={{ minWidth: 0 }}>
                                    {entry.message ? (
                                        <Text size="xs" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {entry.message}
                                        </Text>
                                    ) : null}
                                </Box>
                            </Box>
                        </Group>
                    </>
                ))
            )}
        </>
    );
}

function LineItemFailures({ items }: { items: QuickbooksSyncResult['LineItemFailure'] }) {
    const lineItemDetails = useMemo(() => {
        return (items ?? []).map((f) => ({
            name: f.Name?.trim() || '[Name Empty]',
            message: f.Error ?? '[No Error Details]',
            status: 'failure',
            amount: f.Amount ?? 0,
        }));
    }, [items]);

    return !lineItemDetails.length ? null : (
        <>
            <Text size="xs" weight="bold" color="error" mt="md">
                Line Item Failures
            </Text>
            {lineItemDetails.map((entry, idx) => (
                <Group key={`line-${entry.name}-${idx}`} align="flex-start" spacing="xs" noWrap my={6} px={8} py={6}>
                    <ThemeIcon variant="light" color="error" radius="xl" size="sm">
                        <AlertTriangle size={16} />
                    </ThemeIcon>
                    <Box sx={{ minWidth: 0, flex: 1, userSelect: 'text' }}>
                        <Text size="sm" weight={600} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {entry.name}
                        </Text>
                        {entry.amount ? <Text size="xs">Amount: {entry.amount}</Text> : null}
                        {entry.message ? (
                            <Text size="xs" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {entry.message}
                            </Text>
                        ) : null}
                    </Box>
                </Group>
            ))}
        </>
    );
}
